import React, { useEffect, useRef } from "react";

/**
 * AboutSheet — "Ask or note" (Nov 1).
 *
 * What the standing captions used to say on every visit, said once: who sees
 * questions, that notes can be shared or kept, and where incidents go. It opens the first time someone puts the cursor in Ask
 * or Note, and any time from the ⓘ on the person's page. The test for each
 * line: would she want to know it before she types? Everything else stays off
 * the page.
 *
 *   team      on the team around this person (CAP_TEAM): notes and incidents
 *   signedIn  a signed-in user; a share-link visitor has no name to attach
 */
export function aboutLines({ team, signedIn }) {
  // Wording: David, Nov 1.
  const lines = [signedIn
    ? "Coordinators see each question and who asked it, so they can improve the profile."
    : "Coordinators see each question, so they can improve the profile."];
  if (team) {
    lines.push("Share a note with your coordinator, or keep it just for you.");
    lines.push("If something needs an official report, use the agency's incident process, not this app.");
  }
  return lines;
}

export function aboutTitle({ name, team }) {
  return team ? "Ask or note" : `Asking about ${name || "this person"}`;
}

// One small mark per line, in order: asking, noting, reporting (Nov 1 polish).
const stroke = { fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
const ICONS = [
  <svg key="ask" width="18" height="18" viewBox="0 0 24 24" {...stroke}><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z"/></svg>,
  <svg key="note" width="18" height="18" viewBox="0 0 24 24" {...stroke}><path d="M4 20h4L19 9l-4-4L4 16v4zM14 6l4 4"/></svg>,
  <svg key="report" width="18" height="18" viewBox="0 0 24 24" {...stroke}><path d="M12 9v4M12 17h.01M10.3 3.9L1.8 18.1A2 2 0 0 0 3.5 21h17a2 2 0 0 0 1.7-2.9L13.7 3.9a2 2 0 0 0-3.4 0z"/></svg>,
];

export default function AboutSheet({ open, onClose, name, team = false, signedIn = false }) {
  const btn = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    btn.current?.focus();
    const onKey = e => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div style={s.scrim} onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-labelledby="about-sheet-title" style={s.sheet}
           onClick={e => e.stopPropagation()}>
        <div style={s.grip} aria-hidden="true" />
        <h2 id="about-sheet-title" style={s.title}>{aboutTitle({ name, team })}</h2>
        <ul style={s.list}>
          {aboutLines({ team, signedIn }).map((l, i) => (
            <li key={l} style={s.item}>
              <span style={s.icon} aria-hidden="true">{ICONS[i] || ICONS[0]}</span>
              <span style={s.line}>{l}</span>
            </li>
          ))}
        </ul>
        <button ref={btn} type="button" style={s.ok} onClick={onClose}>Got it</button>
      </div>
    </div>
  );
}

const s = {
  scrim: { position: "fixed", inset: 0, background: "rgba(26,25,21,0.35)", zIndex: 50, display: "flex", alignItems: "flex-end", justifyContent: "center" },
  sheet: { width: "100%", maxWidth: 520, background: "var(--color-surface)", borderRadius: "20px 20px 0 0", padding: "12px 24px max(28px, env(safe-area-inset-bottom))", display: "flex", flexDirection: "column", gap: 16, boxShadow: "0 -4px 24px rgba(26,25,21,0.14)" },
  list:  { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 14 },
  item:  { display: "flex", alignItems: "flex-start", gap: 12 },
  icon:  { width: 32, height: 32, flexShrink: 0, borderRadius: 999, background: "var(--color-accent-subtle)", color: "var(--color-accent)", display: "inline-flex", alignItems: "center", justifyContent: "center" },
  grip:  { width: 36, height: 4, borderRadius: 999, background: "var(--color-border)", alignSelf: "center" },
  title: { margin: "8px 0 0", fontFamily: "var(--font-serif)", fontWeight: 500, fontSize: 22, lineHeight: 1.25, color: "var(--color-text-primary)" },
  line:  { fontSize: "var(--text-base)", lineHeight: 1.45, color: "var(--color-text-primary)", paddingTop: 5 },
  ok:    { marginTop: 4, minHeight: 48, border: 0, borderRadius: 999, background: "var(--color-accent)", color: "var(--color-text-on-accent)", font: "inherit", fontSize: "var(--text-base)", fontWeight: "var(--weight-medium)", cursor: "pointer" },
};
